import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';
import { pdf } from '@react-pdf/renderer';
import { Download, FileText, QrCode as QrCodeIcon, Plus, Trash2, Upload, Gift, HelpCircle, X } from 'lucide-react';
import CertificatePDF from '../components/CertificatePDF';
import PrizesPDF from '../components/PrizesPDF';

interface Code {
  id: string;
  code: string;
  place: string;
  duration: string;
  locked?: boolean; // If true, fields are read-only (from JSON import)
}

const CodeGeneratorPage: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const [tournament, setTournament] = useState('');
  const [codes, setCodes] = useState<Code[]>([
    { id: '1', code: '', place: '1', duration: '12' },
    { id: '2', code: '', place: '2', duration: '6' },
    { id: '3', code: '', place: '3', duration: '3' }
  ]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLanguageChange = (newLang: string) => {
    // Replace the current language in the path with the new language
    const pathParts = location.pathname.split('/').filter(part => part !== '');

    // Check if first part is a language code
    if (pathParts.length > 0 && ['en', 'fr', 'de', 'it'].includes(pathParts[0])) {
      // Replace existing language
      pathParts[0] = newLang;
    } else {
      // Add language at the beginning
      pathParts.unshift(newLang);
    }

    const newPath = '/' + pathParts.join('/') + location.search;
    navigate(newPath);
  };

  // Check for JSON URL parameter on mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jsonUrl = urlParams.get('json');

    if (jsonUrl) {
      loadJsonFromUrl(jsonUrl);
    }
  }, []);

  const loadJsonFromUrl = async (jsonFileName: string) => {
    setLoading(true);
    try {
      // Only allow loading predefined tournament files
      const allowedFiles = [
        'zkm-2025-codes',
        'zkm-2025-nachwuchs-codes',
        'anjtt-codes',
        'schmaetterball-cup-codes',
        'mttv-29nov-2025-codes'
      ];

      // Extract filename without extension
      const fileName = jsonFileName.replace(/\.json$/, '').replace(/^certificate-codes\//, '');

      if (!allowedFiles.includes(fileName)) {
        throw new Error('Tournament file not found');
      }

      // Import the JSON file directly (bundled with the app, not publicly accessible)
      const jsonModule = await import(`../../certificate-codes/${fileName}.json`);
      const json = jsonModule.default;

      if (json.tournament) setTournament(json.tournament);
      if (Array.isArray(json.codes)) {
        const importedCodes = json.codes.map((c: any, idx: number) => ({
          id: (idx + 1).toString(),
          code: c.code || '',
          place: c.place || `${idx + 1}th`,
          duration: c.duration?.toString() || '12',
          locked: true
        }));
        setCodes(importedCodes);
      }
    } catch (error) {
      console.error('Error loading tournament data:', error);
      alert('Failed to load tournament data. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      title: 'Prize Certificate Generator',
      subtitle: 'Create printable certificates with QR codes for tournament winners',
      tournamentName: 'Tournament Name',
      tournamentPlaceholder: 'e.g., Schmätterball-Cup 2024',
      generateCertificates: 'Generate Certificates PDF',
      duration: 'Duration (months)',
      place: 'Place',
      code: 'TTStats Pro Code',
      codePlaceholder: 'TTSTATS-XXXXXXXX',
      generating: 'Generating...',
      addCode: 'Add Prize',
      remove: 'Remove',
      importJson: 'Import JSON',
      infoTitle: 'How it works:',
      info1: 'Enter tournament name and prize codes (or import JSON)',
      info2: 'Adjust place and duration for each prize',
      info3: 'Generate PDF with certificates (one per page, portrait)',
      info4: 'Print and distribute - winners scan QR to claim their code',
      mainTitle: 'YOUR EXCLUSIVE INVITATION',
      heroSubtitle: 'Join the future of Swiss table tennis.',
      heroMessage: 'Track your progress. Analyze your game. Compete smarter.\nBuilt by players, for players. Made in Switzerland.',
      yourReward: 'Your Reward',
      prizeDescription: '',
      tagline: '',
      yourCode: 'Your Personal Access Code',
      scanInstruction: 'Scan to unlock your Pro experience',
      prizePoweredBy: 'In collaboration with',
      monthsText: 'Months',
      worthText: 'Worth',
      placeLabel: 'Prize for',
      membershipText: 'Membership',
      generatePrizesList: 'Generate Prizes Overview PDF',
      prizesListTitle: 'TTStats Pro Prizes',
      prizesListSubtitle: 'Available Prizes for Tournament Winners',
      prizesPlace: 'Place',
      prizesDuration: 'Duration',
      prizesFooter: 'Winners can scan the QR code on their certificate to instantly redeem their TTStats Pro membership',
      howToUse: 'How to Use',
      pdfLanguage: 'PDF Language',
      helpTitle: 'Certificate Generator Guide',
      helpStep1Title: '1. Tournament Data',
      helpStep1: 'If you received a link, your tournament data is already loaded. The codes and durations are locked and cannot be edited.',
      helpStep2Title: '2. Edit Place Labels',
      helpStep2: 'You can customize the "Place" field for each prize (e.g., "Open A - 1st Place", "U18 - Winner", etc.).',
      helpStep3Title: '3. Generate Certificates',
      helpStep3: 'Click "Generate Certificates PDF" to create individual certificates for each winner with QR codes.',
      helpStep4Title: '4. Generate Prizes Overview',
      helpStep4: 'Click "Generate Prizes Overview PDF" to create a summary sheet listing all available prizes - perfect for displaying at your tournament.',
      helpNote: 'Note: Winners can scan the QR code on their certificate to instantly redeem their TTStats Pro membership.',
    },
    de: {
      title: 'Preis-Zertifikat Generator',
      subtitle: 'Druckbare Zertifikate mit QR-Codes für Turniersieger erstellen',
      tournamentName: 'Turniername',
      tournamentPlaceholder: 'z.B. Schmätterball-Cup 2024',
      generateCertificates: 'Zertifikate PDF erstellen',
      duration: 'Dauer (Monate)',
      place: 'Platz',
      code: 'TTStats Pro Code',
      codePlaceholder: 'TTSTATS-XXXXXXXX',
      generating: 'Wird erstellt...',
      addCode: 'Preis hinzufügen',
      remove: 'Entfernen',
      importJson: 'JSON importieren',
      infoTitle: 'So funktioniert es:',
      info1: 'Gib Turniername und Preis-Codes ein (oder importiere JSON)',
      info2: 'Passe Platz und Dauer für jeden Preis an',
      info3: 'Erstelle PDF mit Zertifikaten (eins pro Seite, Hochformat)',
      info4: 'Drucke und verteile - Gewinner scannen QR um ihren Code einzulösen',
      mainTitle: 'DEINE EXKLUSIVE EINLADUNG',
      heroSubtitle: 'Werde Teil der Zukunft des Schweizer Tischtennis.',
      heroMessage: 'Verfolge deinen Fortschritt. Analysiere dein Spiel. Spiele intelligenter.\nVon Spielern, für Spieler. Made in Switzerland.',
      yourReward: 'Deine Belohnung',
      prizeDescription: '',
      tagline: '',
      yourCode: 'Dein Persönlicher Zugangscode',
      scanInstruction: 'Scanne, um dein Pro-Erlebnis freizuschalten',
      prizePoweredBy: 'In Zusammenarbeit mit',
      monthsText: 'Monate',
      worthText: 'Im Wert von',
      placeLabel: 'Preis für',
      membershipText: 'Mitgliedschaft',
      generatePrizesList: 'Preisübersicht PDF erstellen',
      prizesListTitle: 'TTStats Pro Preise',
      prizesListSubtitle: 'Verfügbare Preise für Turniersieger',
      prizesPlace: 'Platz',
      prizesDuration: 'Dauer',
      prizesFooter: 'Gewinner können den QR-Code auf ihrem Zertifikat scannen, um ihre TTStats Pro-Mitgliedschaft sofort einzulösen',
      howToUse: 'Anleitung',
      pdfLanguage: 'PDF Sprache',
      helpTitle: 'Zertifikat Generator Anleitung',
      helpStep1Title: '1. Turnierdaten',
      helpStep1: 'Wenn du einen Link erhalten hast, sind deine Turnierdaten bereits geladen. Die Codes und Dauern sind gesperrt und können nicht bearbeitet werden.',
      helpStep2Title: '2. Platz-Labels bearbeiten',
      helpStep2: 'Du kannst das "Platz"-Feld für jeden Preis anpassen (z.B. "Open A - 1. Platz", "U18 - Sieger", etc.).',
      helpStep3Title: '3. Zertifikate erstellen',
      helpStep3: 'Klicke auf "Zertifikate PDF erstellen", um individuelle Zertifikate für jeden Gewinner mit QR-Codes zu erstellen.',
      helpStep4Title: '4. Preisübersicht erstellen',
      helpStep4: 'Klicke auf "Preisübersicht PDF erstellen", um ein Übersichtsblatt mit allen verfügbaren Preisen zu erstellen - perfekt zum Ausstellen bei deinem Turnier.',
      helpNote: 'Hinweis: Gewinner können den QR-Code auf ihrem Zertifikat scannen, um ihre TTStats Pro-Mitgliedschaft sofort einzulösen.',
    },
    fr: {
      title: 'Générateur de certificats de prix',
      subtitle: 'Créer des certificats imprimables avec QR codes pour les gagnants',
      tournamentName: 'Nom du tournoi',
      tournamentPlaceholder: 'ex: Schmätterball-Cup 2024',
      generateCertificates: 'Générer PDF certificats',
      duration: 'Durée (mois)',
      place: 'Place',
      code: 'Code TTStats Pro',
      codePlaceholder: 'TTSTATS-XXXXXXXX',
      generating: 'Génération...',
      addCode: 'Ajouter un prix',
      remove: 'Supprimer',
      importJson: 'Importer JSON',
      infoTitle: 'Comment ça marche:',
      info1: 'Entre le nom du tournoi et les codes de prix (ou importe JSON)',
      info2: 'Ajuste la place et la durée pour chaque prix',
      info3: 'Génère un PDF avec certificats (un par page, portrait)',
      info4: 'Imprime et distribue - les gagnants scannent le QR pour réclamer leur code',
      mainTitle: 'TON INVITATION EXCLUSIVE',
      heroSubtitle: 'Rejoins le futur du tennis de table suisse.',
      heroMessage: 'Suis ta progression. Analyse ton jeu. Joue plus intelligemment.\nConçu par des joueurs, pour des joueurs. Made in Switzerland.',
      yourReward: 'Ta Récompense',
      prizeDescription: '',
      tagline: '',
      yourCode: 'Ton Code d\'Accès Personnel',
      scanInstruction: 'Scanne pour débloquer ton expérience Pro',
      prizePoweredBy: 'En collaboration avec',
      monthsText: 'Mois',
      worthText: 'D\'une valeur de',
      placeLabel: 'Prix pour',
      membershipText: 'Abonnement',
      generatePrizesList: 'Générer PDF aperçu des prix',
      prizesListTitle: 'Prix TTStats Pro',
      prizesListSubtitle: 'Prix disponibles pour les gagnants du tournoi',
      prizesPlace: 'Place',
      prizesDuration: 'Durée',
      prizesFooter: 'Les gagnants peuvent scanner le QR code sur leur certificat pour activer instantanément leur abonnement TTStats Pro',
      howToUse: 'Mode d\'emploi',
      pdfLanguage: 'Langue PDF',
      helpTitle: 'Guide du générateur de certificats',
      helpStep1Title: '1. Données du tournoi',
      helpStep1: 'Si tu as reçu un lien, les données de ton tournoi sont déjà chargées. Les codes et les durées sont verrouillés et ne peuvent pas être modifiés.',
      helpStep2Title: '2. Modifier les labels de place',
      helpStep2: 'Tu peux personnaliser le champ "Place" pour chaque prix (par ex. "Open A - 1ère place", "U18 - Gagnant", etc.).',
      helpStep3Title: '3. Générer les certificats',
      helpStep3: 'Clique sur "Générer PDF certificats" pour créer des certificats individuels pour chaque gagnant avec codes QR.',
      helpStep4Title: '4. Générer l\'aperçu des prix',
      helpStep4: 'Clique sur "Générer PDF aperçu des prix" pour créer une feuille récapitulative listant tous les prix disponibles - parfait pour afficher lors de ton tournoi.',
      helpNote: 'Note : Les gagnants peuvent scanner le QR code sur leur certificat pour activer instantanément leur abonnement TTStats Pro.',
    },
    it: {
      title: 'Generatore certificati premio',
      subtitle: 'Crea certificati stampabili con QR code per i vincitori',
      tournamentName: 'Nome torneo',
      tournamentPlaceholder: 'es: Schmätterball-Cup 2024',
      generateCertificates: 'Genera PDF certificati',
      duration: 'Durata (mesi)',
      place: 'Posizione',
      code: 'Codice TTStats Pro',
      codePlaceholder: 'TTSTATS-XXXXXXXX',
      generating: 'Generazione...',
      addCode: 'Aggiungi premio',
      remove: 'Rimuovi',
      importJson: 'Importa JSON',
      infoTitle: 'Come funziona:',
      info1: 'Inserisci nome torneo e codici premio (o importa JSON)',
      info2: 'Regola posizione e durata per ogni premio',
      info3: 'Genera PDF con certificati (uno per pagina, verticale)',
      info4: 'Stampa e distribuisci - i vincitori scansionano il QR per richiedere il codice',
      mainTitle: 'IL TUO INVITO ESCLUSIVO',
      heroSubtitle: 'Entra nel futuro del ping pong svizzero.',
      heroMessage: 'Traccia i tuoi progressi. Analizza il tuo gioco. Gioca più intelligente.\nCostruito da giocatori, per giocatori. Made in Switzerland.',
      yourReward: 'Il Tuo Premio',
      prizeDescription: '',
      tagline: '',
      yourCode: 'Il Tuo Codice d\'Accesso Personale',
      scanInstruction: 'Scansiona per sbloccare la tua esperienza Pro',
      prizePoweredBy: 'In collaborazione con',
      monthsText: 'Mesi',
      worthText: 'Del valore di',
      placeLabel: 'Premio per',
      membershipText: 'Abbonamento',
      generatePrizesList: 'Genera PDF panoramica premi',
      prizesListTitle: 'Premi TTStats Pro',
      prizesListSubtitle: 'Premi disponibili per i vincitori del torneo',
      prizesPlace: 'Posizione',
      prizesDuration: 'Durata',
      prizesFooter: 'I vincitori possono scansionare il QR code sul loro certificato per attivare immediatamente il loro abbonamento TTStats Pro',
      howToUse: 'Come usare',
      pdfLanguage: 'Lingua PDF',
      helpTitle: 'Guida generatore certificati',
      helpStep1Title: '1. Dati del torneo',
      helpStep1: 'Se hai ricevuto un link, i dati del tuo torneo sono già caricati. I codici e le durate sono bloccati e non possono essere modificati.',
      helpStep2Title: '2. Modifica etichette posizione',
      helpStep2: 'Puoi personalizzare il campo "Posizione" per ogni premio (es. "Open A - 1° posto", "U18 - Vincitore", ecc.).',
      helpStep3Title: '3. Genera certificati',
      helpStep3: 'Clicca su "Genera PDF certificati" per creare certificati individuali per ogni vincitore con codici QR.',
      helpStep4Title: '4. Genera panoramica premi',
      helpStep4: 'Clicca su "Genera PDF panoramica premi" per creare un foglio riepilogativo con tutti i premi disponibili - perfetto da esporre al tuo torneo.',
      helpNote: 'Nota: I vincitori possono scansionare il QR code sul loro certificato per attivare immediatamente il loro abbonamento TTStats Pro.',
    }
  };

  const t = translations[currentLang] || translations.en;

  const updateCode = (id: string, field: keyof Code, value: string) => {
    setCodes(codes.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const addCode = () => {
    const newId = (Math.max(...codes.map(c => parseInt(c.id))) + 1).toString();
    setCodes([...codes, { id: newId, code: '', place: `${codes.length + 1}th`, duration: '3' }]);
  };

  const removeCode = (id: string) => {
    if (codes.length > 1) {
      setCodes(codes.filter(c => c.id !== id));
    }
  };

  const handleJsonImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        if (json.tournament) setTournament(json.tournament);
        if (Array.isArray(json.codes)) {
          const importedCodes = json.codes.map((c: any, idx: number) => ({
            id: (idx + 1).toString(),
            code: c.code || '',
            place: c.place || `${idx + 1}th`,
            duration: c.duration?.toString() || '12',
            locked: true // Mark imported codes as locked
          }));
          setCodes(importedCodes);
        }
      } catch (error) {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const generatePDF = async () => {
    if (!tournament) {
      alert('Please enter tournament name');
      return;
    }

    if (codes.some(c => !c.code)) {
      alert('Please enter all codes');
      return;
    }

    setGenerating(true);

    try {
      // Generate QR codes for all certificates
      const certificatesData = await Promise.all(
        codes.map(async (codeItem) => {
          const url = `${window.location.origin}/${currentLang}/codes?code=${encodeURIComponent(codeItem.code)}&tournament=${encodeURIComponent(tournament)}&duration=${codeItem.duration}`;
          const qrDataUrl = await QRCode.toDataURL(url, {
            width: 800,
            margin: 2,
            color: {
              dark: '#1C7AD6',
              light: '#FFFFFF'
            }
          });
          return { ...codeItem, qrDataUrl };
        })
      );

      // Generate PDF for each certificate
      for (let i = 0; i < certificatesData.length; i++) {
        const data = certificatesData[i];

        const doc = (
          <CertificatePDF
            tournament={tournament}
            duration={data.duration}
            qrCodeUrl={data.qrDataUrl}
            mainTitle={t.mainTitle}
            heroSubtitle={t.heroSubtitle}
            heroMessage={t.heroMessage}
            yourReward={t.yourReward}
            prizeDescription={t.prizeDescription}
            tagline={t.tagline}
            yourCode={t.yourCode}
            scanInstruction={t.scanInstruction}
            prizePoweredBy={t.prizePoweredBy}
            monthsText={t.monthsText}
            worthText={t.worthText}
            placeLabel={t.placeLabel}
            place={data.place}
            membershipText={t.membershipText}
          />
        );

        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TTStats-Certificate-${tournament.replace(/\s+/g, '-')}-${data.place}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const generatePrizesPDF = async () => {
    if (!tournament) {
      alert('Please enter tournament name');
      return;
    }

    setGenerating(true);

    try {
      const prizes = codes.map(code => ({
        place: code.place,
        duration: code.duration,
      }));

      const doc = (
        <PrizesPDF
          tournament={tournament}
          prizes={prizes}
          title={t.prizesListTitle}
          subtitle={t.prizesListSubtitle}
          placeColumn={t.prizesPlace}
          durationColumn={t.prizesDuration}
          footerText={t.prizesFooter}
          worthText={t.worthText}
          monthsText={t.monthsText}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TTStats-Prizes-Overview-${tournament.replace(/\s+/g, '-')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating prizes PDF:', error);
      alert('Error generating prizes PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-center gap-3">
            <QrCodeIcon className="w-10 h-10 text-[#1C7AD6]" />
            {t.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">{t.subtitle}</p>
        </motion.div>

        {/* Help Button and Language Switcher */}
        <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 mb-8 px-4 sm:px-0">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white dark:bg-gray-800 border-2 border-[#1C7AD6] text-[#1C7AD6] dark:text-[#1C7AD6] rounded-xl font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-all shadow-sm"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-sm sm:text-base">{t.howToUse}</span>
          </button>

          {/* Language Switcher */}
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
            <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">{t.pdfLanguage}:</span>
            <div className="flex gap-1 flex-1 justify-end sm:justify-start">
              {(['en', 'fr', 'de', 'it'] as const).map((langOption) => (
                <motion.button
                  key={langOption}
                  onClick={() => handleLanguageChange(langOption)}
                  className={`px-2 sm:px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                    currentLang === langOption
                      ? 'bg-[#1C7AD6] text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {langOption.toUpperCase()}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Help Modal */}
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 mx-4 sm:mx-0 bg-blue-50 dark:bg-gray-800 border-2 border-blue-200 dark:border-gray-700 rounded-2xl p-4 sm:p-6 relative"
          >
            <button
              onClick={() => setShowHelp(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-blue-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close help"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-2 pr-10">
              <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#1C7AD6] flex-shrink-0" />
              <span className="line-clamp-2">{t.helpTitle}</span>
            </h2>

            <div className="space-y-3 sm:space-y-4">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 text-sm sm:text-base">{t.helpStep1Title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">{t.helpStep1}</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 text-sm sm:text-base">{t.helpStep2Title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">{t.helpStep2}</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 text-sm sm:text-base">{t.helpStep3Title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">{t.helpStep3}</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 text-sm sm:text-base">{t.helpStep4Title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">{t.helpStep4}</p>
              </div>

              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-blue-300 dark:border-gray-600">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 italic leading-relaxed">{t.helpNote}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="flex items-center justify-center gap-3 mb-8 p-4 bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded-xl">
            <div className="w-5 h-5 border-2 border-[#1C7AD6] border-t-transparent rounded-full animate-spin" />
            <span className="text-[#1C7AD6] font-medium">Loading tournament data...</span>
          </div>
        )}

        {/* Tournament Name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {t.tournamentName}
          </label>
          <input
            type="text"
            value={tournament}
            onChange={(e) => setTournament(e.target.value)}
            placeholder={t.tournamentPlaceholder}
            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-[#1C7AD6] focus:outline-none transition-colors text-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
        </motion.div>

        {/* Codes List */}
        <div className="space-y-4 mb-8">
          {codes.map((codeItem, index) => (
            <motion.div
              key={codeItem.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700 shadow-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-5">
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                    {t.code}
                  </label>
                  <input
                    type="text"
                    value={codeItem.code}
                    onChange={(e) => updateCode(codeItem.id, 'code', e.target.value)}
                    placeholder={t.codePlaceholder}
                    readOnly={codeItem.locked}
                    className={`w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-[#1C7AD6] focus:outline-none font-mono text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 ${
                      codeItem.locked ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                    {t.place}
                  </label>
                  <input
                    type="text"
                    value={codeItem.place}
                    onChange={(e) => updateCode(codeItem.id, 'place', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-[#1C7AD6] focus:outline-none text-gray-900 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                    {t.duration}
                  </label>
                  <input
                    type="number"
                    value={codeItem.duration}
                    onChange={(e) => updateCode(codeItem.id, 'duration', e.target.value)}
                    readOnly={codeItem.locked}
                    className={`w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-[#1C7AD6] focus:outline-none text-gray-900 dark:text-white ${
                      codeItem.locked ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div className="md:col-span-2 flex items-end">
                  {codes.length > 1 && (
                    <button
                      onClick={() => removeCode(codeItem.id)}
                      className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t.remove}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4">
          {/* Top Row - Add, Import, Generate Certificates */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={addCode}
              className="flex-1 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-[#1C7AD6] text-[#1C7AD6] rounded-xl font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t.addCode}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              {t.importJson}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleJsonImport}
              className="hidden"
            />
            <button
              onClick={generatePDF}
              disabled={generating || !tournament || codes.some(c => !c.code)}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-[#1C7AD6] to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t.generating}
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  {t.generateCertificates}
                </>
              )}
            </button>
          </div>

          {/* Bottom Row - Generate Prizes Overview */}
          <button
            onClick={generatePrizesPDF}
            disabled={generating || !tournament}
            className="w-full px-6 py-4 bg-gradient-to-r from-[#ff9f10] to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {generating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t.generating}
              </>
            ) : (
              <>
                <Gift className="w-5 h-5" />
                {t.generatePrizesList}
              </>
            )}
          </button>
        </div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 p-6 bg-blue-50 dark:bg-gray-800 rounded-2xl border border-blue-100 dark:border-gray-700"
        >
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-[#1C7AD6] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p className="font-semibold mb-2">{t.infoTitle}</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>{t.info1}</li>
                <li>{t.info2}</li>
                <li>{t.info3}</li>
                <li>{t.info4}</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CodeGeneratorPage;
